import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import WizardSelect from '../ui/WizardSelect';
import { setFlashToast } from '../../utils/flashToast';
import {
  documentMoverLabel,
  forwardUserLabel,
  forwardUserTask,
  getReviewOriginalOwner,
  listDocumentMovementOfficers,
  listEligibleForwardUsers,
} from '../../services/taskForwarding';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

type ForwardTaskModalProps = {
  open: boolean;
  /** CMS-CRC-22: workflowInstanceStageUserTaskId */
  workflowInstanceStageUserTaskId: string;
  /** CMS-AUQ-1: category_id */
  fileCategoryId: string;
  /** CMS-AUQ-1: sub_category_id */
  subFileCategoryId: string;
  onClose: () => void;
  onSuccess: () => void;
};

type LoadState = 'idle' | 'loading' | 'error';

export function ForwardTaskModal({
  open,
  workflowInstanceStageUserTaskId,
  fileCategoryId,
  subFileCategoryId,
  onClose,
  onSuccess,
}: ForwardTaskModalProps) {
  const { t } = useTranslation();
  const [forwardUserId, setForwardUserId] = useState('');
  const [physicalRequired, setPhysicalRequired] = useState(true);
  const [documentMoverId, setDocumentMoverId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const [usersState, setUsersState] = useState<LoadState>('idle');
  const [moversState, setMoversState] = useState<LoadState>('idle');
  const [userOptions, setUserOptions] = useState<{ id: string; label: string }[]>([]);
  const [moverOptions, setMoverOptions] = useState<{ id: string; label: string }[]>([]);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    let active = true;
    // Defer resets to avoid synchronous setState in effect body (eslint rule).
    queueMicrotask(() => {
      if (!active) return;
      // Data dictionary defaults
      setForwardUserId('');
      setPhysicalRequired(true);
      setDocumentMoverId('');
      setErrors({});
      setCancelConfirmOpen(false);

      setUsersState('loading');
      setMoversState('loading');
      setUserOptions([]);
      setMoverOptions([]);
    });

    const userController = new AbortController();
    const moverController = new AbortController();

    void (async () => {
      const taskId = workflowInstanceStageUserTaskId.trim();

      // CMS-CCA-9: If this is a REVIEW task, backend returns the original forward owner.
      if (taskId) {
        const reviewRes = await getReviewOriginalOwner({ taskId, signal: userController.signal });
        if (userController.signal.aborted) return;
        if (reviewRes.success && reviewRes.data.length) {
          const opts = reviewRes.data
            .map(forwardUserLabel)
            .filter((x): x is { id: string; label: string } => Boolean(x));
          if (opts.length) {
            setUserOptions(opts);
            setForwardUserId(opts[0].id);
            setUsersState('idle');
            return;
          }
        }
      }

      // Fallback: regular forward list
      const r = await listEligibleForwardUsers({
        categoryId: fileCategoryId,
        subCategoryId: subFileCategoryId,
        signal: userController.signal,
      });
      if (userController.signal.aborted) return;
      if (!r.success) {
        setUsersState('error');
        setFlashToast({ message: r.message || t('tasks.modals.forward.loadUsersFailed'), variant: 'error' });
        return;
      }
      const opts = r.data
        .map(forwardUserLabel)
        .filter((x): x is { id: string; label: string } => Boolean(x));
      setUserOptions(opts);
      setUsersState('idle');
    })().catch(() => {
      if (!userController.signal.aborted) setUsersState('error');
    });

    void listDocumentMovementOfficers(moverController.signal)
      .then((r) => {
        if (moverController.signal.aborted) return;
        if (!r.success) {
          setMoversState('error');
          setFlashToast({ message: r.message || t('tasks.modals.forward.loadMoversFailed'), variant: 'error' });
          return;
        }
        const opts = r.data
          .map(documentMoverLabel)
          .filter((x): x is { id: string; label: string } => Boolean(x));
        setMoverOptions(opts);
        setMoversState('idle');
      })
      .catch(() => {
        if (!moverController.signal.aborted) setMoversState('error');
      });

    return () => {
      active = false;
      userController.abort();
      moverController.abort();
    };
  }, [open, fileCategoryId, subFileCategoryId, workflowInstanceStageUserTaskId]);

  const canSubmit = useMemo(() => !submitting, [submitting]);

  const userPlaceholder = usersState === 'error' ? t('common.failedToLoad') : t('common.selectUser');
  const moverPlaceholder = moversState === 'error' ? t('common.failedToLoad') : t('tasks.modals.forward.selectDocumentMover');

  const beginCancel = () => {
    if (submitting) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose();
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!workflowInstanceStageUserTaskId.trim()) next.workflowInstanceStageUserTaskId = t('tasks.assign.userTaskIdRequired');
    if (!forwardUserId.trim()) next.forwardUserId = t('tasks.assign.selectUserRequired');
    if (physicalRequired && !documentMoverId.trim()) next.documentMoverId = t('tasks.modals.forward.selectDocumentMoverRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    const res = await forwardUserTask({
      workflowInstanceStageUserTaskId,
      forwardUserId,
      physicalFileMovementRequired: physicalRequired,
      documentMoverId: physicalRequired ? documentMoverId : undefined,
    });
    setSubmitting(false);
    if (!res.success) {
      setFlashToast({ message: res.message || t('tasks.modals.forward.forwardFailed'), variant: 'error' });
      return;
    }
    setFlashToast({ message: t('tasks.modals.forward.forwardSuccess'), variant: 'success' });
    onSuccess();
  };

  return (
    <Modal
      open={open}
      title={t('tasks.modals.forward.title')}
      boxClassName="cfg-assignment-modal"
      onClose={() => {
        beginCancel();
      }}
    >
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('tasks.modals.forward.discardTitle')}
        message={t('tasks.modals.forward.discardMessage')}
        cancelLabel={t('common.no')}
        confirmLabel={t('common.yes')}
        confirmDisabled={submitting}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />
      {errors.workflowInstanceStageUserTaskId ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="alert"
        >
          {errors.workflowInstanceStageUserTaskId}
        </div>
      ) : null}

      <div className="cfg-assignment-form" style={{ padding: 0 }}>
        <div className="ccr-fg">
          <label>
            {t('common.selectUser')} <span className="req">*</span>
          </label>
          <WizardSelect
            id="forward-task-user"
            hasError={Boolean(errors.forwardUserId)}
            value={forwardUserId}
            placeholder={userPlaceholder}
            options={userOptions}
            disabled={usersState === 'error'}
            loading={usersState === 'loading'}
            emptyMessage={t('tasks.assign.noUsersFound')}
            onChange={(id) => {
              setForwardUserId(id);
              setErrors((e) => ({ ...e, forwardUserId: '' }));
            }}
          />
          <FieldError message={errors.forwardUserId} />
        </div>

        <div className="ccr-fg" style={{ marginTop: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={physicalRequired}
              disabled={submitting}
              onChange={(e) => {
                const next = e.target.checked;
                setPhysicalRequired(next);
                if (!next) {
                  setDocumentMoverId('');
                  setErrors((cur) => ({ ...cur, documentMoverId: '' }));
                }
              }}
            />
            {t('tasks.modals.forward.physicalFileMovement')}
          </label>
        </div>

        {physicalRequired ? (
          <div className="ccr-fg" style={{ marginTop: 12 }}>
            <label>
              {t('tasks.modals.forward.selectDocumentMover')} <span className="req">*</span>
            </label>
            <WizardSelect
              id="forward-task-mover"
              hasError={Boolean(errors.documentMoverId)}
              value={documentMoverId}
              placeholder={moverPlaceholder}
              options={moverOptions}
              disabled={moversState === 'error'}
              loading={moversState === 'loading'}
              emptyMessage={t('tasks.assign.noUsersFound')}
              onChange={(id) => {
                setDocumentMoverId(id);
                setErrors((e) => ({ ...e, documentMoverId: '' }));
              }}
            />
            <FieldError message={errors.documentMoverId} />
          </div>
        ) : null}
      </div>

      <div className="cn-footer cn-footer--plain">
        <button className="mfb" type="button" disabled={submitting} onClick={beginCancel}>
          {t('common.cancel')}
        </button>
        <button className="mfb mfb-dk" type="button" disabled={!canSubmit} onClick={() => void submit()}>
          {submitting ? t('tasks.modals.forward.forwarding') : t('tasks.modals.forward.forwardTask')}
        </button>
      </div>
    </Modal>
  );
}

